import { IForm } from "../../Form/IForm";
import {
    EXTERN_CHECK_TASK_EVENT_PATH,
    formGetExternCheckTask,
    formStartExternCheckTask,
} from "../EventManagement/ExternalEvents/ExternalEvents";
import { delay } from "../../utils";
import { requestFailed } from "../../ActionCreators/ErrorLogActionCreators";
import { isNotNullOrEmpty, isNullOrEmpty } from "../../../../../Common/TypingUtils";
import { getFakeErrorLogForDraft } from "../../Fakes/FakeErrorLog";
import { GroupedErrorLog, ServerErrorLog } from "../../../../../Common/CandyApiClient/Types/ErrorLog/ServerErrorLog";
import { track } from "../../../Controls/Internals/Metrics/MetricsProvider";
import { ERROR_LOG_COUNTER } from "../../../Controls/metricsAction";
import { runFloatingAsyncAction } from "../../../Helpers/TypingUtils";
import { AttachInfo } from "../../../../../Common/CandyApiClient/Types/Attachments/Attacments";

import { errorlogFromKewebToGroupedAdapter, serverErrorLogToGroupedAdapter } from "./Utils/Utils";
import { IErrorLogTaskInfo, IErrorLogTaskResult, isHeavyErrorLog } from "./Utils/Models";

export interface IErrorLogApi {
    getErrorLog(getAttachInfo: (attachmentId: string) => AttachInfo | undefined): Promise<GroupedErrorLog>;
    resetStatuses(): void;
}

export class ErrorLogApi implements IErrorLogApi {
    private readonly form: IForm;
    private taskId: string = "";
    private taskStatus: IErrorLogTaskInfo | undefined;
    public constructor(form: IForm) {
        this.form = form;
    }

    public async getErrorLog(
        getAttachInfo: (attachmentId: string) => AttachInfo | undefined
    ): Promise<GroupedErrorLog> {
        let errorLog: GroupedErrorLog;
        let type;
        const start = Date.now();
        const fakeErrorLog = this.getFakeErrorLog();
        if (fakeErrorLog) {
            if (isHeavyErrorLog(fakeErrorLog)) {
                errorLog = errorlogFromKewebToGroupedAdapter(getAttachInfo, fakeErrorLog);
            } else {
                errorLog = serverErrorLogToGroupedAdapter(fakeErrorLog);
            }
        } else if (
            (this.form.settings.useHeavyErrorLog || this.form.settings.isFormInFormNds) &&
            this.form.hasExternalEventSubscriber(EXTERN_CHECK_TASK_EVENT_PATH)
        ) {
            // Флаг для активации нового протокола
            type = "New";
            errorLog = await this.getErrorLogFromKeweb(getAttachInfo);
        } else {
            // Флаг для активации старого протокола
            type = "Old";
            const serverErrorLog = await this.form.candyApiDraftClient.getErrorLog();
            errorLog = serverErrorLogToGroupedAdapter(serverErrorLog);
        }
        const end = Date.now();
        track("candy", ERROR_LOG_COUNTER, {
            errors: errorLog.groups.reduce<number>((acc, it) => acc + it.errors.length, 0),
            warnings: errorLog.groups.reduce<number>((acc, it) => acc + it.warnings.length, 0),
            timeSeconds: (end - start) / 1000,
            type: type,
        });
        this.resetStatuses();
        return errorLog;
    }

    private getFakeErrorLog(): undefined | ServerErrorLog | IErrorLogTaskResult {
        return getFakeErrorLogForDraft(this.form.settings.draftId);
    }

    private async getErrorLogFromKeweb(
        getAttachInfo: (attachmentId: string) => AttachInfo | undefined
    ): Promise<GroupedErrorLog> {
        await this.runErrorLogTask();
        // От этих неочевидных зависимостей избавимся, как только начнем ходить в апи, а не в кевеб
        // Если есть идеи как можно лучше - буду рад критике
        return errorlogFromKewebToGroupedAdapter(getAttachInfo, this.taskStatus?.result);
    }

    private async runErrorLogTask(): Promise<void> {
        await this.setErrorLogTask();
        const isTaskFinished = await this.waitBeforeTaskFinished();
        if (!isTaskFinished) {
            this.resetStatuses();
            await this.runErrorLogTask();
        }
    }

    private async setErrorLogTask(): Promise<void> {
        // Тут вместо вызова колбека будет запрос на апи. Как только Стас вынесет в отдельный сервис
        // https://yt.skbkontur.ru/issue/FP-92/Vstroit-vse-proverki-v-Kendi.-2
        this.form.triggerExternalEvent(formStartExternCheckTask(this.setTaskId.bind(this)));
        //-- Пока туда ходит кевеб - мы будем мониторить
        await this.waitBeforeTaskStands();
    }

    private async waitBeforeTaskStands(): Promise<void> {
        while (isNullOrEmpty(this.taskId)) {
            await delay(500);
        }
    }

    private async waitBeforeTaskFinished(): Promise<boolean> {
        await this.checkErrorLogStatus();
        while (!this.taskStatus?.isFinished) {
            if (this.taskStatus?.needRestart === true) {
                return false;
            } else {
                await delay(500);
            }
        }
        return true;
    }

    private async checkErrorLogStatus(): Promise<void> {
        await delay(1000);
        if (this.taskId) {
            const params = {
                taskId: this.taskId,
                setTaskStatus: this.setTaskStatus.bind(this),
            };
            this.form.triggerExternalEvent(formGetExternCheckTask(params));
            //-- Пока туда ходит кевеб - мы будем сбоку мониторить
        }
    }

    //Это отдаём для кевеба. Потом адаптируем под себя
    private setTaskId(taskId?: string) {
        if (isNullOrEmpty(taskId)) {
            this.setRequestFailedStatus();
        } else {
            this.taskId = taskId;
        }
    }

    private async setTaskStatus(taskStatus?: IErrorLogTaskInfo) {
        if (taskStatus == undefined || isNotNullOrEmpty(taskStatus.errorMessage)) {
            this.setRequestFailedStatus();
        } else if (!taskStatus.isFinished) {
            runFloatingAsyncAction(this.checkErrorLogStatus.bind(this));
            this.taskStatus = taskStatus;
        } else {
            this.taskStatus = taskStatus;
        }
    }
    // Конец кевеба

    private setRequestFailedStatus() {
        this.resetStatuses();
        this.form.store.dispatch(requestFailed());
    }

    public resetStatuses() {
        this.taskId = "";
        this.taskStatus = undefined;
    }
}
